import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../AuthContext.jsx'
import { getSubscription, getMyQuota, getPrice, applyReseller, cancelSubscription } from '../api.js'
import i18n from '../i18n/i18n.js'
import { trackEvent, getUserSegment } from '../analytics.js'
import { useTranslation } from 'react-i18next'
import PaywallModal from '../components/PaywallModal.jsx'
import GrowLabLogo from '../components/GrowLabLogo.jsx'

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [subscription, setSubscription] = useState(null)
  const [quota, setQuota] = useState(null)
  const [price, setPrice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [cancelConfirm, setCancelConfirm] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelError, setCancelError] = useState('')
  const [showSubscribeModal, setShowSubscribeModal] = useState(false)
  const [resellerApplyLoading, setResellerApplyLoading] = useState(false)
  const [resellerApplyError, setResellerApplyError] = useState('')
  const [tawkUnread, setTawkUnread] = useState(false)

  useEffect(() => {
    const onUnread = () => setTawkUnread(true)
    const onRead = () => setTawkUnread(false)
    window.addEventListener('growlab:tawkUnread', onUnread)
    window.addEventListener('growlab:tawkRead', onRead)
    return () => {
      window.removeEventListener('growlab:tawkUnread', onUnread)
      window.removeEventListener('growlab:tawkRead', onRead)
    }
  }, [])

  useEffect(() => {
    async function loadDashboardData() {
      try {
        const [subscriptionResponse, quotaResponse, priceResponse] = await Promise.all([
          getSubscription(),
          getMyQuota(),
          getPrice(),
        ])
        setSubscription(subscriptionResponse.data)
        setQuota(quotaResponse.data)
        setPrice(priceResponse.data)
        await refreshUser()
      } catch {
        setError(t('dashboard.loadError'))
      } finally {
        setLoading(false)
      }
    }
    loadDashboardData()
  }, [])

  async function handleLogout() {
    trackEvent('logout', {
      user_segment: getUserSegment({
        provisionedByReseller: user?.provisioned_by_reseller,
        subscriptionActive: isActive,
        trialTokensRemaining: user?.trial_tokens_remaining ?? 0,
      }),
    })
    await logout()
    navigate('/')
  }

  async function handleCancelConfirm() {
    setCancelLoading(true)
    setCancelError('')
    try {
      await cancelSubscription()
      trackEvent('subscription_cancelled', {
        user_segment: getUserSegment({
          provisionedByReseller: user?.provisioned_by_reseller,
          subscriptionActive: isActive,
          trialTokensRemaining: user?.trial_tokens_remaining ?? 0,
        }),
      })
      const { data } = await getSubscription()
      setSubscription(data)
      setCancelConfirm(false)
    } catch {
      setCancelError(t('dashboard.cancel.error'))
    } finally {
      setCancelLoading(false)
    }
  }

  const isActive =
    subscription?.status === 'active' || subscription?.status === 'trialing'

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })

  const statusSubtext = (() => {
    if (!subscription) return null
    const { status, cancel_at_period_end, current_period_end, trial_end } = subscription
    switch (status) {
      case 'active':
        return cancel_at_period_end
          ? t('dashboard.subscription.subtext.cancelsOn', { date: formatDate(current_period_end) })
          : t('dashboard.subscription.subtext.renewsOn', { date: formatDate(current_period_end) })
      case 'trialing':
        return t('dashboard.subscription.subtext.trialEnds', { date: formatDate(trial_end || current_period_end) })
      case 'past_due':
      case 'unpaid':
        return t('dashboard.subscription.subtext.paymentFailed')
      case 'canceled':
        return t('dashboard.subscription.subtext.canceled')
      case 'incomplete':
        return t('dashboard.subscription.subtext.incomplete')
      case 'incomplete_expired':
        return t('dashboard.subscription.subtext.incompleteExpired')
      case 'paused':
        return t('dashboard.subscription.subtext.paused')
      default:
        return null
    }
  })()

  const formattedPrice = price
    ? (price.amount / 100).toLocaleString(undefined, { style: 'currency', currency: price.currency }) + '/' + price.interval
    : '…'

  if (loading) return <div className="page-loading">{t('dashboard.loading')}</div>

  const pct = quota
    ? Math.min(100, Math.round((quota.output_tokens_used / quota.output_tokens_limit) * 100))
    : 0

  return (
    <div className="dash-page">
      {/* Topbar */}
      <div className="dash-topbar">
        <Link to="/chat" className="dash-back-link">{t('dashboard.backToChat')}</Link>
        <div className="dash-topbar-logo">
          <GrowLabLogo size={20} />
          <span>GROWLAB CLOUD</span>
        </div>
      </div>

      <div className="dash-title">{t('dashboard.title')}</div>

      {error && <p className="dash-error">{error}</p>}

      {/* Cards grid */}
      <div className="dash-cards-grid">
        {/* Account card */}
        <div className="dash-card">
          <div className="card-label">{t('dashboard.account.label')}</div>
          <div className="card-row">
            <span className="card-row-label">{t('dashboard.account.email')}</span>
            <span className="card-row-value">{user?.email ?? '—'}</span>
          </div>
          <div className="card-row">
            <span className="card-row-label">{t('dashboard.account.name')}</span>
            <span className="card-row-value card-row-value--green">{user?.full_name ?? '—'}</span>
          </div>
        </div>

        {/* Subscription details card */}
        <div className="dash-card">
          <div className="card-label">{t('dashboard.subscription.label')}</div>
          <div className="card-row">
            <span className="card-row-label">{t('dashboard.subscription.status')}</span>
            {user?.provisioned_by_reseller
              ? <div className="status-cell">
                  <span className="status-badge active">
                    <span className="status-dot" />
                    {t('dashboard.subscription.statusActive')}
                  </span>
                  <span className="status-subtext">{t('dashboard.subscription.managedByProvider')}</span>
                </div>
              : subscription
              ? <div className="status-cell">
                  <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                    {isActive && <span className="status-dot" />}
                    {isActive ? t('dashboard.subscription.statusActive') : t('dashboard.subscription.statusInactive')}
                  </span>
                  {statusSubtext && <span className="status-subtext">{statusSubtext}</span>}
                </div>
              : <span className="card-row-value muted">—</span>}
          </div>
          <div className="card-row">
            <span className="card-row-label">{t('dashboard.subscription.periodEnd')}</span>
            <span className={`card-row-value${subscription?.current_period_end ? '' : ' muted'}`}>
              {subscription?.current_period_end
                ? new Date(subscription.current_period_end).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
                : '—'}
            </span>
          </div>
          <div className="card-row">
            <span className="card-row-label">{t('dashboard.subscription.cancelAtEnd')}</span>
            <span className="card-row-value">
              {subscription ? (subscription.cancel_at_period_end ? t('dashboard.subscription.yes') : t('dashboard.subscription.no')) : <span className="muted">—</span>}
            </span>
          </div>
        </div>

        {/* Token usage card */}
        <div className="dash-card">
          <div className="card-label">{t('dashboard.usage.label')}</div>
          {isActive && quota ? (
            <>
              <div className="progress-bar">
                <div
                  className={`progress-fill${pct >= 90 ? ' danger' : ''}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="usage-text">
                {t('dashboard.usage.summary', {
                  used: quota.output_tokens_used.toLocaleString(),
                  limit: quota.output_tokens_limit.toLocaleString(),
                  pct,
                })}
              </div>
              <div className="usage-text" style={{ marginTop: 4 }}>
                {t('dashboard.usage.resetsAt')} {new Date(quota.resets_at).toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' })} ({new Date(quota.resets_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' })})
              </div>
              {pct >= 100 && <div className="limit-reached">{t('dashboard.usage.limitReached')}</div>}
            </>
          ) : (
            <div className="usage-text">{t('dashboard.usage.unavailable')}</div>
          )}
        </div>
      </div>

      {/* Trial tokens card */}
      {user?.trial_tokens_remaining > 0 && (
        <div className="dash-card">
          <div className="card-label">{t('dashboard.trial.label')}</div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${user.trial_tokens_granted > 0
                  ? Math.round((user.trial_tokens_remaining / user.trial_tokens_granted) * 100)
                  : 0}%`
              }}
            />
          </div>
          <div className="usage-text">
            {t('dashboard.trial.usage', {
              remaining: user.trial_tokens_remaining.toLocaleString(),
              granted: user.trial_tokens_granted.toLocaleString(),
            })}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {isActive ? (
        <Link to="/chat" style={{ textDecoration: 'none' }}>
          <button className="dash-btn">{t('dashboard.buttons.goToChat')}</button>
        </Link>
      ) : !user?.is_reseller_demo ? (
        <button className="dash-btn" onClick={() => setShowSubscribeModal(true)}>
          {t('dashboard.buttons.subscribeOpen')}
        </button>
      ) : null}

      {showSubscribeModal && (
        <PaywallModal price={price} onClose={() => setShowSubscribeModal(false)} source="dashboard" />
      )}

      {user?.role === 'reseller' && (
        <Link to="/reseller" style={{ textDecoration: 'none' }}>
          <button className="dash-btn-outline">{t('dashboard.buttons.resellerDashboard')}</button>
        </Link>
      )}

      {user?.role !== 'reseller' && (
        <>
          <button
            className="dash-btn-outline"
            onClick={async () => {
              setResellerApplyLoading(true)
              setResellerApplyError('')
              try {
                const { data } = await applyReseller()
                window.location.href = data.checkout_url
              } catch {
                setResellerApplyError('Could not start reseller application. Please try again.')
                setResellerApplyLoading(false)
              }
            }}
            disabled={resellerApplyLoading}
          >
            {resellerApplyLoading ? t('dashboard.buttons.resellerRedirecting') : t('dashboard.buttons.becomeReseller')}
          </button>
          {resellerApplyError && (
            <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: -4 }}>
              {resellerApplyError}
            </div>
          )}
        </>
      )}

      <button className="dash-btn-outline" onClick={handleLogout}>
        {t('dashboard.buttons.logout')}
      </button>

      {/* Footer */}
      <div className="dash-footer">
        <div className="support-text">
          {/* SUPPORT_EMAIL */}
          {t('dashboard.footer.needHelp')} <a href="mailto:growlab.cloud@outlook.com">growlab.cloud@outlook.com</a>
        </div>

        <div className="dash-footer-legal">
          <Link to="/privacy" className="dash-legal-link">{t('dashboard.footer.privacy')}</Link>
          <span className="dash-footer-sep">·</span>
          <Link to="/terms" className="dash-legal-link">{t('dashboard.footer.terms')}</Link>
          <span className="dash-footer-sep">·</span>
          <button className="dash-legal-link footer-support-btn" onClick={() => {
            if (typeof window.Tawk_API?.maximize === 'function') {
              window.Tawk_API.maximize()
              window.dispatchEvent(new CustomEvent('growlab:tawkRead'))
            } else {
              window.location.href = 'mailto:growlab.cloud@outlook.com'
            }
          }}>
            {t('dashboard.footer.support')}
            {tawkUnread && <span className="tawk-unread-dot" />}
          </button>
        </div>

        {isActive && (
          <div className="cancel-area">
            {!cancelConfirm ? (
              <button className="cancel-link" onClick={() => setCancelConfirm(true)}>
                {t('dashboard.cancel.link')}
              </button>
            ) : (
              <div className="confirm-row">
                <span>{t('dashboard.cancel.confirm')}</span>
                <button
                  className="confirm-yes"
                  onClick={handleCancelConfirm}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? t('dashboard.cancel.cancelling') : t('dashboard.cancel.yes')}
                </button>
                <button
                  className="confirm-no"
                  onClick={() => { setCancelConfirm(false); setCancelError('') }}
                  disabled={cancelLoading}
                >
                  {t('dashboard.cancel.no')}
                </button>
              </div>
            )}
            {cancelError && (
              <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 6 }}>{cancelError}</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
